// Recent-events feed: the full filtered list of outbreaks, most recent
// first, in an internally-scrolled list. Each row shows pathogen dot,
// abbreviation, relative age, location, cases + deaths, and a confidence
// badge. Click selects on the map.
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { OutbreakWithSources, Pathogen } from '../types/supabase';
import { pickI18n } from '../types/supabase';
import { PATHOGEN_COLOR, PATHOGEN_LABEL, CONFIDENCE_CLASSES } from '../lib/palette';

const DAY_MS = 86_400_000;

// Localized relative age, escalating to the largest unit that reads
// cleanly: days under a fortnight, then weeks, then months, then years
// (e.g. 21 days → "3 weeks ago", 400 days → "1 year ago"). Handles
// future-dated events too ("in 3 days"). Falls back to the year on an
// unparseable date.
function relativeAge(dateStr: string, lang: string): string {
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return dateStr.slice(0, 4);
  const diffDays = Math.round((Date.now() - then) / DAY_MS);
  const abs = Math.abs(diffDays);
  if (abs === 0) {
    return new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'auto' })
      .format(0, 'day');
  }
  let value = diffDays;
  let unit: Intl.RelativeTimeFormatUnit = 'day';
  if (abs >= 365) { value = Math.round(diffDays / 365); unit = 'year'; }
  else if (abs >= 60) { value = Math.round(diffDays / 30); unit = 'month'; }
  else if (abs >= 14) { value = Math.round(diffDays / 7); unit = 'week'; }
  return new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'always' })
    .format(-value, unit);
}

interface Props {
  outbreaks: OutbreakWithSources[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Inline mode: drop the floating-card chrome and fill the parent flex
   *  column. Used inside the persistent left sidebar. */
  inline?: boolean;
  /** Outbreak data still fetching — show shimmer rows instead of the
   *  (misleading) "no events match" empty state. */
  loading?: boolean;
}

export function EventList({ outbreaks, selectedId, onSelect, inline, loading }: Props) {
  const [open, setOpen] = useState(true);

  // Sorted by occurrence (most recent first). Single ordering — sort UI removed.
  const sorted = useMemo(
    () => [...outbreaks].sort((a, b) => b.start_date.localeCompare(a.start_date)),
    [outbreaks],
  );

  if (inline) {
    return (
      <div className="flex flex-col">
        <div className="px-4 pt-3 pb-2">
          <h3 className="text-[8px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            Events
          </h3>
        </div>
        {!loading && sorted.length === 0 ? (
          <div className="px-4 py-4 text-[12.5px] text-[var(--color-text-3)]">
            No events match the current filters.
          </div>
        ) : (
          <div className="relative">
            <div
              className="overflow-y-auto"
              style={{ maxHeight: 'min(calc(100vh - 460px), 240px)' }}
            >
              {loading ? (
                <EventSkeleton />
              ) : (
                <ul>
                  {sorted.map(o => (
                    <Row
                      key={o.id}
                      outbreak={o}
                      selected={o.id === selectedId}
                      onClick={() => onSelect(o.id)}
                    />
                  ))}
                </ul>
              )}
            </div>
            {/* Soft fade so the scrolled list dissolves into the panel
             *  instead of ending on a hard clipped row. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
          </div>
        )}
      </div>
    );
  }

  // Floating-card mode (legacy) — collapsible header + full list, no sort
  return (
    <div className="surface-glass pointer-events-auto flex w-[300px] flex-col overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex h-9 items-center gap-1.5 border-b border-[var(--color-line)] px-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
      >
        {open ? (
          <ChevronDown size={14} strokeWidth={2} className="text-[var(--color-text-3)]" />
        ) : (
          <ChevronRight size={14} strokeWidth={2} className="text-[var(--color-text-3)]" />
        )}
        <span className="text-[8px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
          Events
        </span>
      </button>

      {open && (
        <div className="max-h-[min(60vh,460px)] overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="px-3 py-4 text-[12.5px] text-[var(--color-text-3)]">
              No events match the current filters.
            </div>
          ) : (
            <ul>
              {sorted.map(o => (
                <Row
                  key={o.id}
                  outbreak={o}
                  selected={o.id === selectedId}
                  onClick={() => onSelect(o.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Shimmer placeholders shaped like Row (title line + metadata line) so the
// list reserves its layout while outbreak data is still in flight.
function EventSkeleton() {
  return (
    <ul aria-busy="true" aria-label="Loading events">
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="flex flex-col gap-1.5 border-b border-[var(--color-line)] px-4 py-2.5 last:border-b-0"
        >
          <div className="shimmer h-3.5 w-3/4 rounded" />
          <div className="shimmer h-3 w-1/2 rounded" />
        </li>
      ))}
    </ul>
  );
}

function Row({ outbreak, selected, onClick }: {
  outbreak: OutbreakWithSources;
  selected: boolean;
  onClick: () => void;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const info = PATHOGEN_LABEL[outbreak.pathogen as Pathogen];
  const color = PATHOGEN_COLOR[outbreak.pathogen as Pathogen] ?? '#a8a29e';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={
          'group flex w-full flex-col gap-0.5 border-b border-[var(--color-line-strong)] px-4 py-2 text-left transition-colors last:border-b-0 ' +
          (selected
            ? 'bg-[var(--color-accent-soft)]'
            : 'hover:bg-[var(--color-surface-2)]')
        }
      >
        <div
          className={
            'mb-1 truncate text-[13px] font-medium ' +
            (selected ? 'text-[var(--color-accent)]' : 'text-[var(--color-text)]')
          }
        >
          {pickI18n(outbreak.location_name_i18n, outbreak.location_name, lang)}
        </div>
        <div className="flex items-center gap-2 text-[13px] text-[var(--color-text-3)]">
          <span className="mono inline-flex items-center gap-2.5 text-[12px] uppercase leading-none tracking-wide">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: color }}
              />
              <span className="font-semibold text-[var(--color-text-2)]">{info.abbreviation}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="tracking-normal text-[var(--color-text-3)]">{relativeAge(outbreak.start_date, lang)}</span>
            </span>
          </span>
          <span
            className={
              'ml-auto rounded-full px-1.5 py-1 text-[9.5px] font-medium uppercase leading-none tracking-wide ' +
              CONFIDENCE_CLASSES[outbreak.confidence]
            }
          >
            {outbreak.confidence}
          </span>
        </div>
      </button>
    </li>
  );
}
